import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {ModalStepper} from 'UI/components/action/ModalStepper';
import {ConsentManager} from 'UI/components/modal/ConsentManager';
import {InitialConsent} from 'UI/components/modal/InitialConsent';

import {useCookieConsent} from './useCookieConsent';
import {Cookie} from 'Images/icons';

import type {WithChildren} from 'types/global';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'CookieConsent'
     */
    testId?: string;
}

/**
 * The CookieConsent component provides a comprehensive cookie consent management system that wraps the application content with cookie handling capabilities.
 * It renders the main cookie manager, floating cookie settings button, and modal dialogs for initial consent and consent management.
 * This component integrates with the CookieManager library to provide GDPR-compliant cookie consent functionality with customizable categories and translations.
 *
 * @param props          The component props.
 * @param props.children The child components to be wrapped by the cookie consent system.
 * @param props.testId   Test identifier for the component, used for testing purposes.
 * @returns The JSX element containing the cookie consent system with wrapped children.
 *
 * @example
 * ```tsx
 * <CookieConsent testId="app-cookie-consent">
 *     <App />
 * </CookieConsent>
 * ```
 */
const CookieConsent = ({children, testId = 'CookieConsent'}: WithChildren<ComponentProps>) => {
    const {
        closeInitModal,
        closeManager,
        InitModal,
        ManagerModal,
        openManager,
        showFloating
    } = useCookieConsent();

    return (
        <>
            {children}
            {showFloating && (
                <FloatingButton aria-label="Cookie Einstellungen" tabIndex={0} onClick={openManager}>
                    <Cookie />
                </FloatingButton>
            )}
            <InitModal>
                <ModalStepper testId={testId}>
                    <InitialConsent handleClose={closeInitModal} />
                    <ConsentManager handleClose={closeInitModal} />
                </ModalStepper>
            </InitModal>
            <ManagerModal>
                <ConsentManager handleClose={closeManager} />
            </ManagerModal>
        </>
    );
};

CookieConsent.displayName = 'CookieConsent';

export {CookieConsent};

const FloatingButton = styled.button`
    align-items: center;
    background-color: ${({theme}) => theme.colors.backgroundLight};
    border: none;
    border: 1px solid ${({theme}) => theme.colors.textNeutral};
    border-radius: 50%;
    bottom: ${spacing(4)};
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    color: ${({theme}) => theme.colors.textNeutral};
    cursor: pointer;
    display: flex;
    height: 3rem;
    justify-content: center;
    left: ${spacing(4)};
    padding: 0;
    position: fixed;
    width: 3rem;
    z-index: 1000;

    & > svg {
        transform: rotate(0deg);
        transition: transform 0.3s ease-in-out;
    }

    &:hover {
        & > svg {
            transform: rotate(24deg);
        }
    }

    &:focus-visible {
        border: 1px solid ${({theme}) => theme.colors.tertiary};
        outline: 1px solid ${({theme}) => theme.colors.tertiary};
    }
`;